import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { CreditCard, Home, MessageCircle, User } from 'lucide-react';
import { TIERS } from './usageTiers';
import { BILLING_STATES, deriveBillingRecord, formatDateMs, formatMoneyMinor } from './billingRecord';
import AccountCommentCell from './AccountCommentCell';

// The Billing view of the Accounts tab.
//
// Everything here comes from what WE have recorded on the account document —
// no Stripe calls. See billingRecord.js for the derivation and why. The
// per-account Billing modal is still the authoritative live view; this table is
// the fleet-wide overview.
//
// Kept in its own file so the Accounts tab in AdminDashboard.js gains only a
// render branch rather than another 200 lines of JSX.

const TIER_LABEL = { warm: 'Warm', glow: 'Glow' };

export default function BillingAccountsTable({
  users,
  records,
  accountUsage,
  signedInCounts,
  billingAttention,
  comments = {},
  commentsUnavailable,
  commentsLoading,
  commentFailedIds,
  onCommentSaved,
  hasDraftForAccount,
  SortHeader,
  onOpenDoc,
  onOpenChat,
  onOpenBilling,
  getUnreadCountForAccount,
  getConversationStatus,
  nowMs = Date.now(),
}) {
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b">
          <SortHeader field="email">Email</SortHeader>
          <SortHeader field="name">Name</SortHeader>
          <SortHeader field="families" align="center">Families</SortHeader>
          <SortHeader field="tier" align="center">Tier</SortHeader>
          <SortHeader field="setUpAt" align="center">Set up</SortHeader>
          <SortHeader field="startDate" align="center">Started</SortHeader>
          <th className="text-center py-1.5 px-2 font-medium text-sm">Amount</th>
          <SortHeader field="billingState" align="center">Status</SortHeader>
          {/* Fixed width, matching AccountCommentCell's own box, so the column
              is the same size whether or not a cell is being edited. */}
          <th className="text-left py-1.5 px-2 font-medium text-sm w-[236px]">Comment</th>
          <th className="text-center py-1.5 px-2 font-medium text-sm">Messages</th>
          <th className="text-right py-1.5 px-2 font-medium text-sm">Billing</th>
        </tr>
      </thead>
      <tbody>
        {users.map((user) => {
          // Derived once by the parent (it also sorts on these); the fallback
          // keeps this component usable on its own.
          const rec = records?.[user.id] || deriveBillingRecord(user, nowMs);
          const stateMeta = BILLING_STATES[rec.state] || BILLING_STATES.none;
          const tierInfo = TIERS[accountUsage[user.id]?.tier ?? 5];
          const attentionReasons = [...new Set([
            ...(billingAttention[user.id] || []),
            ...(user.subscriptionCancelRequested ? ['cancel_requested'] : []),
          ])];

          return (
            <tr key={user.id} className="border-b hover:bg-muted/50 transition-colors">
              <td className="py-1.5 px-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <span
                      className="font-medium truncate block text-blue-600 hover:underline cursor-pointer"
                      onClick={() => onOpenDoc({ type: 'account', id: user.id })}
                    >
                      {user.email}
                    </span>
                    <span
                      className="text-xs text-muted-foreground block hover:text-blue-600 hover:underline cursor-pointer"
                      onClick={() => onOpenDoc({ type: 'account', id: user.id })}
                    >
                      {user.id}
                    </span>
                  </div>
                </div>
              </td>

              <td className="py-1.5 px-2 text-sm">{user.nameAtOrder || '-'}</td>

              <td className="py-1.5 px-2 text-center">
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                  <Home className="w-3 h-3" />
                  <span>{signedInCounts[user.id] ?? 0}/{user.numOfFamilies ?? 0}</span>
                </div>
              </td>

              <td className="py-1.5 px-2 text-center">
                <Badge className={`text-xs ${tierInfo.badgeClass}`} title={`${tierInfo.name} — ${tierInfo.description}`}>
                  {tierInfo.label}
                </Badge>
              </td>

              {/* Set up: yes/no and the date. Deliberately not who did it —
                  the admin email is noise in a table one person maintains; the
                  Billing modal still shows it. */}
              <td className="py-1.5 px-2 text-center text-sm">
                {rec.setUp ? (
                  <>
                    <div className="text-green-700">Yes</div>
                    <div className="text-[10px] text-muted-foreground">{formatDateMs(rec.setUpAtMs)}</div>
                  </>
                ) : (
                  <span className="text-muted-foreground">No</span>
                )}
              </td>

              {/* Started: yes/no, with the date it did or will start */}
              <td className="py-1.5 px-2 text-center text-sm">
                {!rec.setUp ? (
                  <span className="text-muted-foreground">—</span>
                ) : rec.started ? (
                  <>
                    <div className="text-green-700">Yes</div>
                    <div className="text-[10px] text-muted-foreground">{formatDateMs(rec.startMs)}</div>
                  </>
                ) : (
                  <>
                    <div className="text-muted-foreground">No</div>
                    <div className="text-[10px] text-muted-foreground">
                      {rec.startMs != null ? `starts ${formatDateMs(rec.startMs)}` : 'no start date'}
                    </div>
                  </>
                )}
              </td>

              <td className="py-1.5 px-2 text-center text-sm">
                {rec.monthlyMinor != null ? (
                  <>
                    <div>{formatMoneyMinor(rec.monthlyMinor, rec.currency)}/mo</div>
                    <div className="text-[10px] text-muted-foreground">
                      {rec.tier ? (TIER_LABEL[rec.tier] || rec.tier) : '—'}
                      {rec.discountPercent != null ? ` · ${rec.discountPercent}% off` : ''}
                    </div>
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>

              <td className="py-1.5 px-2 text-center">
                <Badge className={`text-xs ${stateMeta.badgeClass}`} title={stateMeta.description}>
                  {stateMeta.label}
                </Badge>
                {rec.state === 'cancelled' && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {formatDateMs(rec.canceledAtMs)}
                  </div>
                )}
                {rec.cancelRequested && (
                  <div className="text-[10px] text-red-600 mt-0.5" title="The customer has asked to cancel">
                    cancel requested
                  </div>
                )}
              </td>

              <td className="py-1.5 px-2">
                {/* onRetry is the same single-account re-read a save triggers —
                    a locked cell can't save, so it needs its own way back. */}
                <AccountCommentCell
                  accountUUID={user.id}
                  comment={comments[user.id]}
                  onSaved={onCommentSaved}
                  onRetry={onCommentSaved}
                  disabled={commentsUnavailable}
                  loading={commentsLoading}
                  failed={!!commentFailedIds?.has(user.id)}
                />
              </td>

              <td className="py-1.5 px-2 text-center">
                <div className="flex justify-center">
                  <Button variant="ghost" size="sm" onClick={() => onOpenChat(user)} className="relative flex items-center gap-1">
                    <MessageCircle className="w-4 h-4" />
                    {getUnreadCountForAccount(user.id) > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                        {getUnreadCountForAccount(user.id) > 9 ? '9+' : getUnreadCountForAccount(user.id)}
                      </span>
                    )}
                    {getUnreadCountForAccount(user.id) === 0 && getConversationStatus(user.id) === 'open' && (
                      <span className="absolute -top-0.5 -right-0.5 bg-green-500 rounded-full h-2.5 w-2.5"></span>
                    )}
                    {/* Saved draft — own corner, so it never hides the unread/open
                        state. Must match the Default and Usage views: all three
                        views show the same button and must say the same thing. */}
                    {hasDraftForAccount && hasDraftForAccount(user.id) && (
                      <span
                        title="You have a saved draft for this account"
                        className="absolute -bottom-0.5 -left-0.5 bg-blue-500 rounded-full h-2.5 w-2.5"
                      ></span>
                    )}
                  </Button>
                </div>
              </td>

              <td className="py-1.5 px-2">
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenBilling({ id: user.id, email: user.email })}
                    title={attentionReasons.length
                      ? `Needs attention: ${attentionReasons.join(', ')}`
                      : 'Review billing & manage subscription'}
                    className="relative flex items-center gap-2"
                  >
                    <CreditCard className="w-3 h-3" />
                    Billing
                    {attentionReasons.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 rounded-full h-2.5 w-2.5"></span>
                    )}
                  </Button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
